/**
 * One-step "back" for hosts that own a hardware back button (Android
 * WebView). The parent posts `navigate_back`; the iframe undoes the topmost
 * open layer — a modal, the reveal, a delivery form, a sub-view — or reports
 * that it is already at its root so the host can close the WebView.
 *
 * The app has no router: every layer is a local useState closed by its own
 * callback. So each open layer registers that callback here, and back runs
 * the top one. "Top" is decided by LEVEL first and registration order second:
 * effects run child-first, so on a deep-link mount (`/my-packs/deliveries/new`)
 * the delivery route registers BEFORE the My Packs view that contains it —
 * pure LIFO would close the view and skip the form.
 *
 * `blocking` layers (a purchase or sell in flight) swallow back without doing
 * anything: answering "not handled" there would let the host close the WebView
 * mid-purchase.
 */
export type BackLevel = 'view' | 'subroute' | 'overlay' | 'blocking';

const RANK: Record<BackLevel, number> = {
  view: 0,
  subroute: 1,
  overlay: 2,
  blocking: 3,
};

interface Entry {
  rank: number;
  seq: number;
  run: () => void;
}

const entries = new Set<Entry>();
let nextSeq = 0;

export function registerBackHandler(level: BackLevel, run: () => void): () => void {
  const entry: Entry = { rank: RANK[level], seq: nextSeq++, run };
  entries.add(entry);
  return () => {
    entries.delete(entry);
  };
}

/** Runs the topmost handler. Returns false when nothing is open (at root). */
export function runBack(): boolean {
  let top: Entry | null = null;
  for (const entry of entries) {
    if (!top || entry.rank > top.rank || (entry.rank === top.rank && entry.seq > top.seq)) {
      top = entry;
    }
  }
  if (!top) return false;
  top.run();
  return true;
}
